import PropTypes from 'prop-types';
// @mui

import { useNavigate } from 'react-router-dom';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { LinearProgress, MenuItem } from '@mui/material';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { PROJECT_EDITOR_ROLE } from '../../../../Constants/ProjectConstants';
import { isAdmin } from '../../../../utils/share';
import ConfirmModal from './ConfirmModal';
import Label from '../../../../components/label';
import Iconify from '../../../../components/iconify';
import { convertHM } from '../../../../utils/timeCalculation';
import {
  addFavoriteProject,
  deleteFavoriteProject,
  getFavoriteProject,
  updateProject,
} from '../../../../services/projectService';
import CustomPopover, { usePopover } from '../../../../components/custom-popover';

// ----------------------------------------------------------------------

export default function ProjectTableRow({
  row,
  selected,
  fetchTableData,
  favoriteProjectIds,
  setProjectCounts,
  refetchHeaderCount,
}) {
  // eslint-disable-next-line camelcase

  const navigate = useNavigate();
  // eslint-disable-next-line camelcase
  const { id, projectName, clients, totalHrs, total_time, status, projectPercentage } = row;
  const [anchorEl, setAnchorEl] = useState(null);
  const [action, setAction] = useState('');
  const [openPopup, setOpenPopup] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isFavoriteProject, setIsFavoriteProject] = useState(false);
  //   const confirm = useBoolean();

  const popover = usePopover();

  const handleUpdateModalClose = () => setOpenPopup(false);
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedProject(null);
  };

  const handleMarkAsArchived = (project) => {
    setSelectedProject(project);
    setAction('archive');
    setOpenPopup(true);
  };
  const handleMarkAsActive = (project) => {
    setSelectedProject(project);
    setAction('active');
    setOpenPopup(true);
  };

  const handleMarkAsCompleted = (project) => {
    setSelectedProject(project);
    setAction('complete');
    setOpenPopup(true);
  };

  const handleMarkAsHold = (project) => {
    setSelectedProject(project);
    setAction('hold');
    setOpenPopup(true);
  };

  const isEditPermission =
    isAdmin() || PROJECT_EDITOR_ROLE.includes(selectedProject?.projectmember?.projectRoles?.roleName);
  const handleProjectStatusChange = async (project, status, successMessage, dates = {}) => {
    if (!isEditPermission) {
      toast.error('You do not have permission to perform this action');
      handleCloseMenu();
      return;
    }
    if (selectedProject?.status === status) {
      toast.success(`Project is already ${status}`);
      handleCloseMenu();
      return;
    }
    try {
      await updateProject(project.id, { status, ...dates });
      fetchTableData();
      toast.success(successMessage);
      handleCloseMenu();
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || 'An error occurred while updating the project. Please try again later.';
      toast.error(errMsg);
    }
  };

  const handleUpdate = (values) => {
    if (action === 'complete') {
      handleProjectStatusChange(selectedProject, 'complete', 'Project marked as completed');
      setOpenPopup(false);
    }
    if (action === 'archive') {
      handleProjectStatusChange(selectedProject, 'archive', 'Project marked as archived');
      setOpenPopup(false);
    }
    if (action === 'active') {
      handleProjectStatusChange(selectedProject, 'active', 'Project marked as active');
      setOpenPopup(false);
    }
    if (action === 'hold') {
      const { holdStartDate, holdEndDate } = values;
      handleProjectStatusChange(selectedProject, 'hold', 'Project marked as on hold', { holdStartDate, holdEndDate });
      setOpenPopup(false);
    }
  };

  const navigateToProjectDetails = (id, projectName) => {
    navigate(`/project/details/${id}`, { state: { name: projectName } });
  };

  const handleFavoriteProject = async () => {
    if (!isEditPermission) {
      toast.error('You do not have permission to perform this action');
      return;
    }

    try {
      const data = {
        projectId: id,
      };
      if (isFavoriteProject) {
        setProjectCounts((prev) => ({
          ...prev,
          favoriteCount: isFavoriteProject ? prev.favoriteCount - 1 : prev.favoriteCount + 1,
        }));
        await deleteFavoriteProject(data);
      } else {
        setProjectCounts((prev) => ({
          ...prev,
          favoriteCount: prev.favoriteCount + 1,
        }));
        await addFavoriteProject(data);
      }

      // First update the local state
      setIsFavoriteProject(!isFavoriteProject);

      // Then refresh the data
      await refetchHeaderCount();
      // await fetchTableData();
    } catch (error) {
      // Revert local state if API call fails
      setIsFavoriteProject(isFavoriteProject);
      toast.error(error.response?.data?.message || 'An error occurred');
    }
  };

  useEffect(() => {
    setIsFavoriteProject(favoriteProjectIds.includes(id));
  }, [favoriteProjectIds, id]);

  return (
    <>
      <TableRow hover selected={selected}>
        {isAdmin() && (
          <TableCell sx={{ cursor: 'pointer' }} onClick={handleFavoriteProject}>
            {isFavoriteProject ? (
              <Iconify icon="eva:star-fill" sx={{ color: 'blue' }} />
            ) : (
              <Iconify icon="eva:star-outline" sx={{ color: 'text.disabled' }} />
            )}
          </TableCell>
        )}

        <TableCell sx={{ alignItems: 'center' }}>
          <Typography
            variant="body2"
            noWrap
            sx={{ textTransform: 'capitalize', textDecoration: 'underline', cursor: 'pointer' }}
            onClick={() => navigateToProjectDetails(id, projectName)}
          >
            {projectName}
          </Typography>
        </TableCell>

        <TableCell sx={{ textTransform: 'capitalize' }}>{clients.clientName}</TableCell>

        <TableCell>{totalHrs}</TableCell>

        <TableCell>{convertHM(total_time)}</TableCell>

        <TableCell style={{ width: '150px' }}>
          {/* Increase the width of the TableCell as needed */}
          <div style={{ display: 'flex', alignItems: 'center', width: '100%', cursor: 'pointer' }}>
            <LinearProgress
              variant="determinate"
              value={projectPercentage > 100 ? 100 : projectPercentage}
              color={projectPercentage < 71 ? 'success' : projectPercentage < 91 ? 'warning' : 'error'}
              style={{
                height: 20,
                width: '70%', // Adjust the width as needed
                border: projectPercentage < 91 ? undefined : '1px solid #e43b3b',
              }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              style={{ marginLeft: '10px' }} // Add some space between the progress bar and the text
            >
              {`${Math.round(projectPercentage)}%`}
            </Typography>
          </div>
        </TableCell>

        <TableCell>
          <Label
            variant="soft"
            color={
              (status === 'active' && 'warning') ||
              (status === 'archive' && 'info') ||
              (status === 'complete' && 'success') ||
              (status === 'hold' && 'error') ||
              'default'
            }
          >
            {status}
          </Label>
        </TableCell>

        <TableCell align="center" sx={{ px: 1 }}>
          <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
            <Iconify icon="eva:more-vertical-fill" />
          </IconButton>
        </TableCell>
        {openPopup && (
          <ConfirmModal
            updateSelected={selectedProject}
            onClose={handleUpdateModalClose}
            onUpdate={handleUpdate}
            action={action}
          />
        )}
      </TableRow>

      <CustomPopover open={popover.open} onClose={popover.onClose} arrow="right-top" sx={{ width: 170 }}>
        <MenuItem onClick={() => handleMarkAsArchived(row)}>
          <Typography variant="body2" fontSize="14px">
            Mark As Archived
          </Typography>
        </MenuItem>

        <MenuItem onClick={() => handleMarkAsActive(row)}>
          <Typography variant="body2" fontSize="14px">
            Mark As Active
          </Typography>
        </MenuItem>

        <MenuItem onClick={() => handleMarkAsCompleted(row)}>
          <Typography variant="body2" fontSize="14px">
            Mark As Completed
          </Typography>
        </MenuItem>

        <MenuItem onClick={() => handleMarkAsHold(row)}>
          <Typography variant="body2" fontSize="14px">
            Mark As Hold
          </Typography>
        </MenuItem>
      </CustomPopover>
    </>
  );
}

ProjectTableRow.propTypes = {
  row: PropTypes.object,
  selected: PropTypes.bool,
};
